import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { RefreshCwIcon } from "lucide-react";

import { useRetryReportCell } from "../../hooks/useRetryReportCell";
import { useGridContext } from "../../../hooks/useEvaluateGridContext";
import { useCellDisplayData } from "../hooks/useCellDisplayData";

import { ReportCell, ReportColumn } from "@/types/evaluate";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries";

export const FailedCell = ({ cell, column }: { cell: ReportCell; column: ReportColumn }) => {
  const { isEditDisabled } = useRbac();
  const cannotEditReport = isEditDisabled(EntityType.REPORT);

  const { retryReportCell, isLoading: retryIsLoading } = useRetryReportCell(cell?.id);
  const { tableStore } = useGridContext();
  const selectedCell = tableStore?.selectedCell;

  const { displayValue } = useCellDisplayData(cell, column, false, selectedCell);

  const handleRetry = () => {
    if (!retryIsLoading) {
      retryReportCell();
    }
  };

  return (
    <div className="flex h-fit flex-1 flex-col gap-3 bg-red-50 p-3">
      <div className="mb-1 flex w-full items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2 text-xs font-medium text-red-700">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-red-600" />
            Execution Failed
          </div>
          <div className="whitespace-pre-wrap text-xs text-red-600">
            {displayValue || "An error occurred during execution"}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center pt-2">
        <BasicTooltip
          content={cannotEditReport ? "You don't have permission to retry cells" : undefined}
          shouldShow={cannotEditReport}
        >
          <span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRetry}
              disabled={retryIsLoading || cannotEditReport}
              className={cn(
                "h-8 px-3 text-xs font-medium",
                "text-red-700 hover:text-red-800",
                "hover:bg-red-100 active:bg-red-200",
                "transition-all duration-200",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <RefreshCwIcon
                className={cn("mr-1.5 h-3.5 w-3.5", retryIsLoading && "animate-spin")}
              />
              {retryIsLoading ? "Retrying..." : "Retry"}
            </Button>
          </span>
        </BasicTooltip>
      </div>
    </div>
  );
};
